// Frontend Logger Utility
class Logger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000; // Keep last 1000 logs
    this.isEnabled = true;
    this._idCounter = 0;
  }

  // Format timestamp
  getTimestamp() {
    return new Date().toISOString();
  }

  // Add log entry
  addLog(level, component, message, data = null) {
    if (!this.isEnabled) return;

    const logEntry = {
      timestamp: this.getTimestamp(),
      level,
      component,
      message,
      data: data ? JSON.stringify(data, null, 2) : null,
      id: `${Date.now()}-${++this._idCounter}`
    };

    this.logs.unshift(logEntry); // Add to beginning
    
    // Keep only maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(0, this.maxLogs);
    }

    // Also log to console with color coding
    const consoleMessage = `[${level.toUpperCase()}] [${component}] ${message}`;

    if (level === 'error') console.error(consoleMessage);
    else if (level === 'warn') console.warn(consoleMessage);
    else if (level === 'info') console.info(consoleMessage);
    else console.debug(consoleMessage);

    // Trigger custom event for log viewer
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('newLog', { detail: logEntry }));
    }
  }

  // Convenience methods
  error(component, message, data) {
    this.addLog('error', component, message, data);
  }

  warn(component, message, data) {
    this.addLog('warn', component, message, data);
  }

  info(component, message, data) {
    this.addLog('info', component, message, data);
  }

  debug(component, message, data) {
    this.addLog('debug', component, message, data);
  }

  // Get all logs
  getAllLogs() {
    return this.logs;
  }

  // Get logs by component
  getLogsByComponent(component) {
    return this.logs.filter(log => log.component === component);
  }

  // Get logs by level
  getLogsByLevel(level) {
    return this.logs.filter(log => log.level === level);
  }

  // Clear logs
  clearLogs() {
    this.logs = [];
    console.clear();
  }

  // Export logs as JSON
  exportLogs() {
    const dataStr = JSON.stringify(this.logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `8d-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Enable/disable logging
  setEnabled(enabled) {
    this.isEnabled = enabled;
  }
}

// Create singleton instance
const logger = new Logger();

// Email-specific logging helpers
export const emailLogger = {
  teamCreation: (data) => logger.info('EMAIL-TEAM', 'Team creation email triggered', data),
  responsiblePerson: (data) => logger.info('EMAIL-RESPONSIBLE', 'Responsible person email triggered', data),
  emailSuccess: (messageId, type) => logger.info('EMAIL-SUCCESS', `Email sent successfully: ${type}`, { messageId }),
  emailError: (error, type) => logger.error('EMAIL-ERROR', `Email failed: ${type}`, error),
  apiCall: (endpoint, payload) => logger.debug('EMAIL-API', `API call to ${endpoint}`, payload),
  apiResponse: (endpoint, response) => logger.debug('EMAIL-API', `API response from ${endpoint}`, response)
};

// D1 specific logging helpers
export const d1Logger = {
  saveStart: (data) => logger.info('D1-SAVE', 'D1 save started', data),
  saveSuccess: (data) => logger.info('D1-SAVE', 'D1 save completed', data),
  saveError: (error) => logger.error('D1-SAVE', 'D1 save failed', error),
  memberAdd: (member) => logger.debug('D1-MEMBER', 'Member added', member),
  memberUpdate: (member) => logger.debug('D1-MEMBER', 'Member updated', member)
};

// D2 specific logging helpers
export const d2Logger = {
  responsibleSet: (data) => logger.info('D2-RESPONSIBLE', 'Responsible person set', data),
  formSave: (data) => logger.info('D2-FORM', 'D2 form saved', data),
  formError: (error) => logger.error('D2-FORM', 'D2 form error', error)
};

// API logging helpers
export const apiLogger = {
  request: (url, method, data) => logger.debug('API-REQUEST', `${method} ${url}`, data),
  response: (url, status, data) => logger.debug('API-RESPONSE', `${status} ${url}`, data),
  error: (url, error) => logger.error('API-ERROR', `Error ${url}`, error)
};

export default logger;